// PickUpCpu.js
// Play CPU — the solo Pick Up Draft screen. Same flow as the multiplayer draft, but the
// opponent is the computer and the whole game runs locally (no Firestore, no Cloud Function).
//
// Reads ?sport=&difficulty= from the URL, loads that sport's players, and builds an in-memory
// game with buildCpuGame(). Human picks flow through applyPick(); when it becomes the CPU's
// turn, a 1-second "thinking" pause runs cpuChooseMove() and applies its pick (chaining
// naturally when the snake order gives the CPU two picks in a row). When the game finishes it
// hands the finished game object to the existing PickUpComplete screen via navigation state.
//
// Reachable at /battles/pickup/cpu?sport=<id>&difficulty=<normal|hard|expert>.

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../managers/AuthContext";
import PickUpDraftBoard from "../components/PickUpDraftBoard";
import { fetchPlayersForSport } from "../managers/playerService";
import { currentTurn } from "../managers/pickupService";
import {
  buildCpuGame,
  applyPick,
  cpuChooseMove,
  CPU_ID,
  DIFFICULTIES,
} from "../managers/cpuOpponent";

const VALID_SPORTS = ["football", "baseball", "basketball", "hockey", "soccer"];

export default function PickUpCpu() {
  const { user } = useAuth();
  const uid = user?.uid || null;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const sportId = searchParams.get("sport") || "";
  const difficulty = searchParams.get("difficulty") || "normal";

  const [game, setGame] = useState(null);
  const [error, setError] = useState("");
  const navigatedRef = useRef(false);

  const validRequest = VALID_SPORTS.includes(sportId) && !!DIFFICULTIES[difficulty];

  // Build the local game once, after players load.
  useEffect(() => {
    if (!uid || !validRequest) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const players = await fetchPlayersForSport(sportId);
        if (cancelled) return;
        setGame(
          buildCpuGame({
            sportId,
            difficulty,
            humanUID: uid,
            humanUsername: user?.username || "YOU",
            allPlayers: players,
          })
        );
      } catch (_) {
        if (!cancelled) setError("Couldn't load players for this sport. Please try again.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, sportId, difficulty, validRequest, user]);

  // CPU turn — pause ~1s so it feels like it's thinking, then make its pick. After setGame
  // this effect re-runs; if it's still the CPU's turn (snake can give it two in a row) it
  // schedules again, so consecutive CPU picks chain automatically.
  useEffect(() => {
    if (!game || game.status !== "active") return undefined;
    if (currentTurn(game) !== CPU_ID) return undefined;
    const t = window.setTimeout(() => {
      setGame((g) => {
        if (!g || g.status !== "active" || currentTurn(g) !== CPU_ID) return g;
        const move = cpuChooseMove(g, difficulty);
        if (!move) return g;
        return applyPick(g, { userID: CPU_ID, roundIndex: g.currentRoundIndex, ...move });
      });
    }, 1000);
    return () => window.clearTimeout(t);
  }, [game, difficulty]);

  // When finished, hand the completed game to the existing results screen.
  useEffect(() => {
    if (!game || game.status !== "complete" || navigatedRef.current) return;
    navigatedRef.current = true;
    window.setTimeout(() => {
      navigate("/battles/pickup/complete", { state: { localGame: game } });
    }, 600);
  }, [game, navigate]);

  // The human's pick.
  const handlePick = (position, slotInRound, player) => {
    setGame((g) => {
      if (!g || g.status !== "active" || currentTurn(g) !== uid) return g;
      return applyPick(g, { userID: uid, roundIndex: g.currentRoundIndex, position, slotInRound, player });
    });
  };

  if (!uid) {
    return (
      <div style={styles.page}>
        <p style={styles.dim}>Play CPU needs a Rosterable account.</p>
        <button style={styles.primaryBtn} onClick={() => navigate("/login")}>SIGN IN</button>
      </div>
    );
  }

  if (!validRequest) {
    return (
      <div style={styles.page}>
        <p style={styles.dim}>That game isn't set up correctly.</p>
        <button style={styles.primaryBtn} onClick={() => navigate("/battles/pickup")}>BACK</button>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.page}>
        <p style={styles.dim}>{error}</p>
        <button style={styles.primaryBtn} onClick={() => navigate("/battles/pickup")}>BACK</button>
      </div>
    );
  }

  if (!game) {
    return (
      <div style={styles.page}>
        <style>{`@keyframes cpu-spin { to { transform: rotate(360deg); } }`}</style>
        <div style={styles.loadingWrap}>
          <span style={styles.spinner} />
          <p style={{ ...styles.dim, marginTop: 0 }}>Building draft…</p>
        </div>
      </div>
    );
  }

  return (
    <PickUpDraftBoard
      game={game}
      uid={uid}
      onPick={handlePick}
      onBack={() => navigate("/battles/pickup")}
      showTimer={false}
    />
  );
}

const styles = {
  page: { padding: "16px 16px 96px", maxWidth: 560, margin: "0 auto", color: "#e8e9ec", minHeight: "100vh", textAlign: "center" },
  loadingWrap: { display: "flex", flexDirection: "column", alignItems: "center", gap: 14, marginTop: 60 },
  spinner: { width: 30, height: 30, borderRadius: 15, border: "3px solid #d4f53c", borderTopColor: "transparent", display: "inline-block", animation: "cpu-spin 0.7s linear infinite" },
  dim: { fontSize: 14, color: "#6e747e", marginTop: 40 },
  primaryBtn: { marginTop: 12, padding: "12px 18px", borderRadius: 10, border: "none", background: "#d4f53c", color: "#0c0d10", fontWeight: 800, letterSpacing: 1, cursor: "pointer" },
};
